import { makeAutoObservable } from 'mobx';
import { isDirectory, isFile, type Node } from '../../types/admin/nodes';
import { MediaLibraryService } from 'services/Admin/MediaLibraryService';

const compareByType = (a: Node, b: Node) => {
  if (a.type === b.type) return 0;
  return a.type === 'file' ? 1 : -1;
};

const compareByName = (a: Node, b: Node) => {
  return a.name.localeCompare(b.name, undefined, { numeric: true });
}

const compare = (a: Node, b: Node) => {
  const typeComparison = compareByType(a, b);
  if (typeComparison !== 0) return typeComparison;

  return compareByName(a, b);
}

export class MediaLibraryStore {
  allNodes: Node[] = [];
  currentDirectoryId = '_root';
  selectedIds = new Set<string>();
  chosenFileId: string | null = null;
  searchQuery = '';
  selectionAnchorId: string | null = null;
  isLoading = false;

  constructor(private readonly fileManagerService: MediaLibraryService) {
    makeAutoObservable(this);
  }

  private setAllNodes = (nodes: Node[]) => {
    this.allNodes = nodes.sort(compare);
  }

  fetchNodes = async () => {
    try {
      this.isLoading = true;
      const nodes = await this.fileManagerService.getNodes();

      this.setAllNodes(nodes);
    } catch (error) {
    //
    }
    this.isLoading = false;
  }

  get selectedNodes(): Node[] {
    return this.allNodes.filter(n => this.selectedIds.has(n.id));
  }

  get currentNodes() {
    const q = this.searchQuery.trim().toLowerCase();

    return this.allNodes.filter(n =>
      n.parentId === this.currentDirectoryId &&
      n.parentId !== '_trash' &&
      (q === '' || n.name.toLowerCase().includes(q))
    );
  }

  get currentFiles() {
    return this.currentNodes.filter(isFile);
  }

  get currentDirectories() {
    return this.currentNodes.filter(isDirectory);
  }

  select = (ids: string[]) => {
    this.selectedIds.clear();
    ids.forEach(id => this.selectedIds.add(id));
    this.selectionAnchorId = ids.at(-1) ?? null;
  };

  appendSelection = (id: string) => {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.selectionAnchorId = id;
  };

  selectRangeTo = (targetId: string) => {
    const allIds = this.currentNodes.map(n => n.id);
    const startId = this.selectionAnchorId ?? targetId;

    const startIndex = allIds.indexOf(startId);
    const endIndex = allIds.indexOf(targetId);
    if (startIndex === -1 || endIndex === -1) return;

    const [from, to] = [startIndex, endIndex].sort((a, b) => a - b);
    const range = allIds.slice(from, to + 1);

    range.forEach(id => this.selectedIds.add(id));
  };

  setSearchQuery = (query: string) => this.searchQuery = query;

  chooseFile = (id: string) => this.chosenFileId = id;

  navigateTo = (id: string) => {
    this.currentDirectoryId = id;
    this.selectedIds.clear();
  };

  createFolder = async (name: string) => {
    const newFolder = await this.fileManagerService.createDirectory(name, this.currentDirectoryId);

    this.setAllNodes([...this.allNodes, newFolder]);
  };

  uploadFiles = async (files: { parentId: string; data: File }[]) => {
    const uploadPromises = files.map(file => this.fileManagerService.uploadFile(file.data, file.parentId));

    const uploaded = await Promise.all(uploadPromises);
    this.setAllNodes([...this.allNodes, ...uploaded]);
  }

  deleteSelected = async () => {
    await this.fileManagerService.removeNodes(Array.from(this.selectedIds));

    this.setAllNodes(this.allNodes.map(n =>
      this.selectedIds.has(n.id)
        ? { ...n, parentId: '_trash', updatedAt: new Date().toISOString() }
        : n
    ));

    this.selectedIds.clear();

    if (this.chosenFileId && this.allNodes.find(n => n.id === this.chosenFileId)?.parentId === '_trash') {
      this.chosenFileId = null;
    }
  };

  renameNode = async (id: string, newName: string) => {
    await this.fileManagerService.renameNode(id, newName);

    this.setAllNodes(this.allNodes.map(node =>
      node.id === id
        ? { ...node, name: newName, updatedAt: new Date().toISOString() }
        : node
    ));
  };

  moveSelectedTo = async (targetId: string) => {
    await this.fileManagerService.moveNodes(Array.from(this.selectedIds), targetId);

    this.setAllNodes(this.allNodes.map(node =>
      this.selectedIds.has(node.id)
        ? { ...node, parentId: targetId, updatedAt: new Date().toISOString() }
        : node
    ));
    this.selectedIds.clear();
  };

  // Check if a target directory is valid for moving selected items
  isValidMoveTarget = (targetId: string): boolean => {
    // Can't move to current directory
    if (targetId === this.currentDirectoryId) return false;
    
    // Can't move to a selected item
    if (this.selectedIds.has(targetId)) return false;
    
    // Can't move to a descendant of any selected folder (prevents circular references)
    return !this.isDescendantOfSelected(targetId);
  };

  // Check if targetId is a descendant of any selected directory
  private isDescendantOfSelected = (targetId: string): boolean => {
    const getDescendants = (folderId: string): string[] => {
      const descendants: string[] = [];
      const children = this.allNodes.filter(node => node.parentId === folderId);
      
      for (const child of children) {
        descendants.push(child.id);
        if (isDirectory(child)) {
          descendants.push(...getDescendants(child.id));
        }
      }
      
      return descendants;
    };

    // Check if targetId is a descendant of any selected folder
    for (const selectedId of this.selectedIds) {
      const selectedNode = this.allNodes.find(n => n.id === selectedId);
      if (selectedNode && isDirectory(selectedNode)) {
        const descendants = getDescendants(selectedId);
        if (descendants.includes(targetId)) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Get ancestor IDs for a given directory (used for expanding folder tree)
  getAncestorIds = (dirId: string): string[] => {
    const path: string[] = [];
    let id = dirId;

    while (id && id !== '_root') {
      // eslint-disable-next-line no-loop-func
      const dir = this.allNodes.find(n => n.id === id && n.type === 'directory');
      if (!dir) break;
      path.unshift(dir.id);
      id = dir.parentId;
    }

    return path;
  };

  // Check if a directory has child directories
  hasChildDirectories = (parentId: string): boolean => {
    return this.allNodes.some(node => 
      isDirectory(node) && node.parentId === parentId && node.id !== '_trash'
    );
  };

  get breadcrumbPath(): string[] {
    const path: string[] = [];
    let currentId = this.currentDirectoryId;

    while (currentId && currentId !== '_root') {
      // eslint-disable-next-line no-loop-func
      const dir = this.allNodes.find(n => n.id === currentId && n.type === 'directory');
      if (!dir) break;
      path.unshift(dir.name);
      currentId = dir.parentId;
    }

    return path;
  }

  get breadcrumbTrail(): { id: string; name: string }[] {
    const trail: { id: string; name: string }[] = [];
    let currentId = this.currentDirectoryId;

    while (currentId && currentId !== '_root') {
      // eslint-disable-next-line no-loop-func
      const dir = this.allNodes.find(n => n.id === currentId && n.type === 'directory');
      if (!dir) break;
      trail.unshift({ id: dir.id, name: dir.name });
      currentId = dir.parentId;
    }

    return trail;
  }
}
